import reactUpdate from 'immutability-helper';
import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import * as EventConstants from 'constants/EventConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as FileConstants from 'folder-items/FileConstants';
// @ts-expect-error
import {extractChildren, mergeChildren} from 'lib/StoreTransform';
import {StructuredData} from 'notebook/data-tables/DataTableConstants';
import {BenchlingAction} from 'redux-stores/Action';
import apiutil from 'util/APIUtil';
import {
  getUiState,
  WorkflowTemplateSelectionState,
} from 'workflows/template-selection/reduceWorkflowTemplateSelection';
import {getParentStageRuns} from 'workflows/util/StageRunUtil';

import {CreateWorkflowState} from './template-selection/WorkflowTemplateSelectionConstants';
import {
  ExperimentalCondition,
  ServerStageEntry,
  StageConfiguration,
  StageEntrySummaryData,
  StageFlowNode,
  StageRun,
  StageRunSample,
  StageRunSampleKey,
  Workflow,
  WORKFLOW_SCREEN,
  WorkflowDashboardDetails,
  WorkflowScreen,
  WorkflowStage,
  WorkflowSummary,
  WorkflowTemplate,
  WorkflowTemplateSummary,
  WorkflowTemplateVersion,
  WorkflowTemplateVersionSummary,
} from './WorkflowConstants';

interface ChildrenByType {
  experimentalConditions: Array<keyof FullData>;
  stageConfigurations: Array<keyof FullData>;
  workflowTemplates: Array<keyof FullData>;
  workflowTemplateVersions: Array<keyof FullData>;
  workflows: Array<keyof FullData>;
  workflowStages: Array<keyof FullData>;
  stageEntries: Array<keyof FullData>;
  stageFlowNodes: Array<keyof FullData>;
  stageRuns: Array<keyof FullData>;
  stageRunSamples: Array<keyof FullData>;
}

const CHILDREN_BY_TYPE: ChildrenByType = {
  experimentalConditions: [],
  stageConfigurations: ['stageFlowNodes', 'experimentalConditions'],
  workflowTemplates: ['workflowTemplateVersions'],
  workflowTemplateVersions: ['stageConfigurations'],
  workflows: ['workflowStages'],
  workflowStages: ['stageEntries'],
  stageEntries: ['stageRuns'],
  stageFlowNodes: [],
  stageRuns: [],
  stageRunSamples: [],
};

interface FullData {
  experimentalConditions: {[id: string]: ExperimentalCondition};
  stageConfigurations: {[id: string]: StageConfiguration};
  workflowTemplates: {[id: string]: WorkflowTemplate};
  workflowTemplateVersions: {[id: string]: WorkflowTemplateVersion};
  workflows: {[id: string]: Workflow};
  workflowStages: {[id: string]: WorkflowStage};
  stageEntries: {[id: string]: ServerStageEntry};
  stageFlowNodes: {[id: string]: StageFlowNode};
  stageRuns: {[id: string]: StageRun};
  stageRunSamples: {[id: string]: StageRunSample};
}

type ItemType = keyof FullData;

const INITIAL_FULL_DATA = _.mapObject(CHILDREN_BY_TYPE, () => ({})) as unknown as FullData;

export interface ViewState<Screen extends WorkflowScreen> {
  screen: Screen;
  params: ViewStateParams<Screen>;
}

export type ViewStateParams<Screen extends WorkflowScreen> = Screen extends WorkflowScreen.WORKFLOW_DETAILS
  ? {workflowId: string}
  : Screen extends WorkflowScreen.WORKFLOW_TEMPLATE_VIEW
    ? {workflowTemplateId: string; selectedWorkflowTemplateVersionId: string | null}
    : {};

type WorkflowsObject =
  | ExperimentalCondition
  | StageConfiguration
  | Workflow
  | WorkflowStage
  | WorkflowTemplate
  | WorkflowTemplateVersion
  | ServerStageEntry
  | StageFlowNode
  | StageRun
  | StageRunSample;

interface SummaryData {
  experimentalConditions: {[id: string]: {}};
  stageConfigurations: {[id: string]: {}};
  workflowTemplates: {[id: string]: WorkflowTemplateSummary};
  workflowTemplateVersions: {[id: string]: WorkflowTemplateVersionSummary};
  workflows: {[id: string]: WorkflowSummary};
  workflowStages: {[id: string]: {}};
  stageEntries: {[id: string]: {}};
  stageFlowNodes: {[id: string]: {}};
  stageRuns: {[id: string]: {}};
  stageRunSamples: {[id: string]: {}};
}

export interface WorkflowsState {
  viewState: ViewState<WorkflowScreen>;
  allWorkflowIds: Array<string> | undefined;
  allWorkflowTemplateIds: Array<string> | undefined;
  fullData: FullData;
  summaryData: SummaryData;
  entryIdToStructuredData: {[entryId: string]: StructuredData};
  entryIdToSummaryData: {[entryId: string]: StageEntrySummaryData};
  entryIdToOrderedInputSampleKeys: {[entryId: string]: Array<StageRunSampleKey>};
  entryIdToOrderedOutputSampleKeys: {[entryId: string]: Array<StageRunSampleKey>};
  stageIdToOrderedStageRunIds: {[stageId: string]: Array<string>};
  workflowIdToDashboardDetails: {[workflowId: string]: WorkflowDashboardDetails};
  // Map of the form {stageRunSampleId => boolean}, with true meaning that it is currently
  // being fetched.
  loadingStageRunSampleIds: {[stageRunSampleId: string]: boolean};
}

export const WORKFLOWS_INITIAL_STATE: WorkflowsState = {
  viewState: {
    screen: WorkflowScreen.WORKFLOW_DASHBOARD,
    params: {},
  },
  allWorkflowIds: undefined,
  allWorkflowTemplateIds: undefined,
  // Map of the form {type => {id => full dict}} (with children normalized)
  fullData: INITIAL_FULL_DATA,
  // Map of the form {type => {id => summary dict}}
  summaryData: {
    ...INITIAL_FULL_DATA,
  },
  entryIdToStructuredData: {},
  entryIdToSummaryData: {},
  entryIdToOrderedInputSampleKeys: {},
  entryIdToOrderedOutputSampleKeys: {},
  stageIdToOrderedStageRunIds: {},
  // Map of the form {workflow ID => WORKFLOW_DASHBOARD_DETAILS_SHAPE}
  workflowIdToDashboardDetails: {},
  // Map of the form {stageRunSampleId => boolean}, with true meaning that it is currently
  // being fetched.
  loadingStageRunSampleIds: {},
};

/**
 * Short-lived class with methods to be used as selectors on the state tree.
 *
 * The state variable may be mutated within the duration of a reducer call.
 */
export class WorkflowSelector {
  state: WorkflowsState;
  constructor(state: WorkflowsState) {
    this.state = state;
  }

  getViewState(): ViewState<WorkflowScreen> {
    return this.state.viewState;
  }

  /*
   * Map the strings in objTypes to the associated summaryData objects.
   * TODO(T9068): I think this can be simplified, both in terms of the implementation and interface.
   */
  _keysToSummaryData(objTypes: Array<keyof SummaryData>): SummaryData {
    return _.chain(objTypes)
      .indexBy((val) => val)
      .mapObject((val) => this.state.summaryData[val])
      .value() as unknown as SummaryData;
  }

  /*
   * Merge in summary data of form {type => {id => object}}, e.g. {workflows: {1: workflowSummary}}
   */
  _mergeSummaryData(childMap: Partial<SummaryData>): void {
    this.state.summaryData = reactUpdate(
      this.state.summaryData,
      _.mapObject(childMap, (childrenById) => ({$merge: childrenById}))
    );
  }

  /*
   * Update the fetched object and the summaries of its children.
   */
  _handleUpdateData(newObjs: Array<WorkflowsObject>, objType: ItemType): void {
    let childMap = this._keysToSummaryData(CHILDREN_BY_TYPE[objType]);

    for (const orig of newObjs) {
      const [obj, children] = extractChildren(orig, childMap);
      this.state.fullData = reactUpdate(this.state.fullData, {
        [objType]: {[obj.id]: {$set: obj}},
      });
      // Assuming that summaryData is always a subset of fullData, so it is safe to store the
      // fullData of the newObj as their summaryData.
      // We are doing this to make sure that summaryData always has the most recent data,
      // so if the fullData of a given object is updated, we also update the summarData with the same values.
      this.state.summaryData = reactUpdate(this.state.summaryData, {
        [objType]: {[obj.id]: {$set: obj}},
      });
      childMap = children;
    }

    this._mergeSummaryData(childMap);
  }

  /*
   * Merge in summary data of form {type => {id => object}} and use it to merge into full data.
   */
  _handleUpdateSummaryData(childMap: Partial<SummaryData>): void {
    this._mergeSummaryData(childMap);
    // TODO(T9484): Fix this hack
    // Only update full data with the summary dicts of objects that have a full dict
    const filteredChildMap = _.mapObject(childMap, (objsById, objType: keyof FullData) =>
      // eslint-disable-next-line no-restricted-syntax
      _.pick(objsById, (obj: WorkflowsObject) => this.state.fullData[objType][obj.id!])
    );
    this.state.fullData = reactUpdate(
      this.state.fullData,
      _.mapObject(filteredChildMap, (childrenById) => _.mapObject(childrenById, (child) => ({$merge: child})))
    );
  }

  _get<T extends keyof FullData, S extends string>(objType: T, id: S): FullData[T][S] | undefined {
    return this.state.fullData[objType][id]
      ? mergeChildren(this.state.fullData[objType][id], this._keysToSummaryData(CHILDREN_BY_TYPE[objType]))
      : undefined;
  }

  getWorkflow(workflowId: string): Workflow | undefined {
    return this._get('workflows', workflowId);
  }

  getWorkflowStage(workflowStageId: string): WorkflowStage | undefined {
    return this._get('workflowStages', workflowStageId);
  }

  getAllWorkflowTemplates(): Array<WorkflowTemplate> | null {
    if (this.state.allWorkflowTemplateIds == null) {
      return null;
    }
    return this.state.allWorkflowTemplateIds.map(
      (workflowTemplateId) => this.getWorkflowTemplate(workflowTemplateId)!
    );
  }

  getWorkflowTemplateSummary(workflowTemplateId: string): WorkflowTemplateSummary | undefined {
    return this.state.summaryData.workflowTemplates[workflowTemplateId];
  }

  getWorkflowTemplate(workflowTemplateId: string): WorkflowTemplate | undefined {
    return this._get('workflowTemplates', workflowTemplateId);
  }

  getWorkflowTemplateVersion(workflowTemplateVersionId: string): WorkflowTemplateVersion | undefined {
    return this._get('workflowTemplateVersions', workflowTemplateVersionId);
  }

  getStageConfiguration(stageConfigurationId: string): StageConfiguration | undefined {
    return this._get('stageConfigurations', stageConfigurationId);
  }

  getStageConfigurations(workflowTemplateVersionId: string): Array<StageConfiguration> | undefined {
    const workflowTemplateVersion = this.getWorkflowTemplateVersion(workflowTemplateVersionId);
    return workflowTemplateVersion
      ? _.compact(
          workflowTemplateVersion.stageConfigurations.map((stageConfigurationSummary) =>
            this.getStageConfiguration(stageConfigurationSummary.id)
          )
        )
      : undefined;
  }

  getWorkflowStages(workflowId: string): Array<WorkflowStage> | undefined {
    const workflow = this.getWorkflow(workflowId);
    return workflow
      ? _.compact(
          workflow.workflowStages.map((workflowStageSummary) =>
            this.getWorkflowStage(workflowStageSummary.id)
          )
        )
      : undefined;
  }

  getStageEntry(stageEntryId: string): ServerStageEntry | undefined {
    return this._get('stageEntries', stageEntryId);
  }

  getStageEntries(workflowStage: WorkflowStage): Array<ServerStageEntry> {
    return workflowStage.stageEntries.map((stageEntrySummary) => this.getStageEntry(stageEntrySummary.id)!);
  }

  stageEntryIsLoading(stageEntryId: string): boolean {
    const stageEntry = this._get('stageEntries', stageEntryId);
    return !stageEntry || !stageEntry.stageRuns;
  }

  getStageRunsForEntry(stageEntryId: string): Array<StageRun> {
    const stageEntry = this._get('stageEntries', stageEntryId);
    return stageEntry ? stageEntry.stageRuns.map(({id}) => this._get('stageRuns', id)!) : [];
  }

  getOrderedStageRunsForEntry(stageEntryId: string): Array<StageRun> {
    let stageRuns = this.getStageRunsForEntry(stageEntryId);
    if (stageRuns.length > 0) {
      const stageEntry = this._get('stageEntries', stageEntryId)!;
      const orderedStageRunIds = this.state.stageIdToOrderedStageRunIds[stageEntry.workflowStageId];
      if (
        !assertAndContinue(
          orderedStageRunIds != null,
          'Expected the stage run order to be loaded. Is the stage entry loaded?'
        )
      ) {
        return stageRuns;
      }
      stageRuns = _.sortBy(stageRuns, (stageRun) =>
        orderedStageRunIds.findIndex((stageRunId) => stageRunId === stageRun.id)
      );
    }
    return stageRuns;
  }

  // Returns map of the form {stage ID => stage entry full dicts} for all stages in given workflow
  getStageIdToStageEntries(workflowId: string): {[stageId: string]: Array<ServerStageEntry>} {
    return this._mapWorkflowStages(workflowId, (workflowStage: WorkflowStage) =>
      this.getStageEntries(workflowStage)
    );
  }

  getStageRun(stageRunId: string): StageRun | undefined {
    return this._get('stageRuns', stageRunId);
  }

  getStageRunsById(stageRunIds: Array<string>): {[id: string]: StageRun} {
    const stageRunsById: {[id: string]: StageRun} = {};
    for (const stageRunId of stageRunIds) {
      const stageRun = this.getStageRun(stageRunId);
      if (stageRun) {
        stageRunsById[stageRun.id] = stageRun;
      }
    }
    return stageRunsById;
  }

  getStageRuns(stageEntry: ServerStageEntry): Array<StageRun> {
    return stageEntry.stageRuns.map((stageRunSummary) => this.getStageRun(stageRunSummary.id)!);
  }

  /**
   * Given an array of stage runs, build a mapping from stage run ID to array of parent IDs.
   *
   * This requires all stage runs, all parents, and all relevant flow nodes to be loaded. If any of
   * these are not loaded, we return null and the UI should render a loading state.
   */
  getParentIdsByStageRunId(stageRunIds: Array<string>): {[id: string]: Array<string>} | null {
    const parentIdsByStageRunId: {[id: string]: Array<string>} = {};
    for (const stageRunId of stageRunIds) {
      parentIdsByStageRunId[stageRunId] = [];
      const stageRun = this.getStageRun(stageRunId);
      if (!stageRun) {
        return null;
      }
      for (const parentStageRun of getParentStageRuns(
        stageRun,
        Object.values(this.state.fullData.stageRuns)
      )) {
        if (parentStageRun == null) {
          return null;
        }
        parentIdsByStageRunId[stageRunId].push(parentStageRun.id);
      }
    }
    return parentIdsByStageRunId;
  }

  getStageRunSample(stageRunSampleId: string): StageRunSample | undefined {
    return this._get('stageRunSamples', stageRunSampleId);
  }

  getStageRunSampleById(): {[id: string]: StageRunSample} {
    return this.state.fullData.stageRunSamples;
  }

  getStageFlowNode(stageFlowNodeId: string): StageFlowNode | undefined {
    return this._get('stageFlowNodes', stageFlowNodeId);
  }

  // Returns a map of the form {stage ID => object} where object is computed by calling the argument
  _mapWorkflowStages<T>(
    workflowId: string,
    workflowStageToObject: (ws: WorkflowStage) => T
  ): {[id: string]: T} {
    const workflowStages = this.getWorkflowStages(workflowId);
    const stageMap: {[id: string]: T} = {};
    if (!workflowStages) {
      return stageMap;
    }
    for (const workflowStage of workflowStages) {
      stageMap[workflowStage.id] = workflowStageToObject(workflowStage);
    }
    return stageMap;
  }

  // Return a map of the form {configuration ID => object} where object is computed by calling the argument
  _mapStageConfigurations<T>(
    workflowTemplateVersion: string,
    stageConfigurationToObject: (sc: StageConfiguration) => T
  ): {[id: string]: T} {
    const stageConfigurations = this.getStageConfigurations(workflowTemplateVersion);
    const stageMap: {[id: string]: T} = {};
    if (!stageConfigurations) {
      return stageMap;
    }
    for (const stageConfiguration of stageConfigurations) {
      stageMap[stageConfiguration.id] = stageConfigurationToObject(stageConfiguration);
    }
    return stageMap;
  }

  // Returns map of the form {stage ID => stage run full dicts} for all stages in given workflow
  getStageIdToStageRuns(workflowId: string): {[stageId: string]: Array<StageRun>} {
    return this._mapWorkflowStages(workflowId, (workflowStage) =>
      this.getStageRunsForStageId(workflowStage.id)
    );
  }

  getStageRunsForStageId(workflowStageId: string): Array<StageRun> {
    const workflowStage = this.getWorkflowStage(workflowStageId)!;
    const stageEntries = this.getStageEntries(workflowStage);
    // eslint-disable-next-line no-restricted-syntax
    return _.flatten(stageEntries.map((stageEntry) => this.getStageRuns(stageEntry)));
  }

  // Return map of the form {configuration ID => stage flow node full dicts} for all stage configurations
  // in a given workflow
  getStageConfigurationIdToStageFlowNodes(workflowTemplateVersionId: string): {
    [id: string]: Array<StageFlowNode>;
  } {
    return this._mapStageConfigurations(workflowTemplateVersionId, (stageConfiguration) =>
      stageConfiguration.stageFlowNodes.map(
        (stageFlowNodeSummary) => this.getStageFlowNode(stageFlowNodeSummary.id)!
      )
    );
  }

  getStageIdToStructuredData(workflowId: string): {[stageId: string]: StructuredData} {
    return this._mapWorkflowStages(workflowId, (workflowStage) => {
      const stageEntries = this.getStageEntries(workflowStage);

      const structuredData = [];
      for (const stageEntry of stageEntries) {
        const entryStructuredData = this.state.entryIdToStructuredData[stageEntry.id];

        if (entryStructuredData) {
          structuredData.push(...entryStructuredData);
        }
      }
      return structuredData;
    });
  }

  getEntryIdToSummaryData(): {[entryId: string]: StageEntrySummaryData} {
    return this.state.entryIdToSummaryData;
  }

  getOrderedInputSampleKeys(stageEntryId: string): Array<StageRunSampleKey> {
    return this.state.entryIdToOrderedInputSampleKeys[stageEntryId];
  }

  getOrderedOutputSampleKeys(stageEntryId: string): Array<StageRunSampleKey> {
    return this.state.entryIdToOrderedOutputSampleKeys[stageEntryId];
  }

  getStageIdToStageConfiguration(workflowId: string): {[id: string]: StageConfiguration} {
    return this._mapWorkflowStages(
      workflowId,
      (workflowStage) => this.getStageConfiguration(workflowStage.stageConfiguration.id)!
    );
  }

  getWorkflowTemplateVersionsById(): {[id: string]: WorkflowTemplateVersion} {
    const workflowTemplateVersionIds = Object.keys(this.state.fullData.workflowTemplateVersions);
    return _.object(
      workflowTemplateVersionIds.map((workflowTemplateVersionId) => [
        workflowTemplateVersionId,
        this._get('workflowTemplateVersions', workflowTemplateVersionId),
      ])
    );
  }

  getWorkflowTemplateVersionById(workflowTemplateVersionId: string): WorkflowTemplateVersion | undefined {
    return this._get('workflowTemplateVersions', workflowTemplateVersionId);
  }

  getWorkflowTemplateSelectionState(
    workflowTemplateSelection: WorkflowTemplateSelectionState
  ): CreateWorkflowState {
    const workflowTemplates = this.getAllWorkflowTemplates();
    const workflowTemplateVersionsById = this.getWorkflowTemplateVersionsById();
    return getUiState(workflowTemplates, workflowTemplateVersionsById, workflowTemplateSelection);
  }

  isStageRunSampleIdLoading(stageRunSampleId: string): boolean {
    return this.state.loadingStageRunSampleIds[stageRunSampleId];
  }
}

export default function reduceWorkflows(
  state: WorkflowsState = WORKFLOWS_INITIAL_STATE,
  action: BenchlingAction
): WorkflowsState {
  switch (action.type) {
    case EventConstants.WORKFLOW_WORKFLOW_DASHBOARD_SCREEN_OPEN: {
      return {
        ...state,
        viewState: {
          screen: WorkflowScreen.WORKFLOW_DASHBOARD,
          params: {},
        },
      };
    }
    case EventConstants.WORKFLOW_WORKFLOW_DASHBOARD_TEMPLATES_SCREEN_OPEN: {
      return {
        ...state,
        viewState: {
          screen: WorkflowScreen.WORKFLOW_DASHBOARD_TEMPLATES,
          params: {},
        },
      };
    }
    case EventConstants.WORKFLOW_WORKFLOW_DETAILS_SCREEN_OPEN: {
      const {workflowId} = action;
      return {
        ...state,
        viewState: {
          screen: WorkflowScreen.WORKFLOW_DETAILS,
          params: {workflowId},
        },
      };
    }
    case EventConstants.WORKFLOW_WORKFLOW_TEMPLATE_VIEW_SCREEN_OPEN: {
      const {workflowTemplateId} = action;
      return {
        ...state,
        viewState: {
          screen: WorkflowScreen.WORKFLOW_TEMPLATE_VIEW,
          params: {
            workflowTemplateId,
            selectedWorkflowTemplateVersionId: null,
          },
        },
      };
    }

    case EventConstants.WORKFLOW_WORKFLOW_SUMMARIES_FETCH_COMPLETED: {
      const {workflowSummaries} = action;
      // Many Workflows-related components unnecessarily depend on the entire workflows redux state, partly
      // because we have to use a WorkflowSelector to access the subset of data that it is actually depended
      // on. This is exacerbated by our sidepanel listings which will always emit this action even if no
      // workflows are returned. What's worse is that components like DataTableEditable that can be used
      // outside of a workflow end up being negatively impacted even if is not being used in a workflow.
      // As a stop-gap solution, we avoid re-render if no workflows are fetched simply by no-oping here.
      if (workflowSummaries.length === 0) {
        return state;
      }
      const newState = {...state};
      for (const {id} of workflowSummaries) {
        if (!newState.allWorkflowIds) {
          newState.allWorkflowIds = [id];
        } else if (!newState.allWorkflowIds.includes(id)) {
          newState.allWorkflowIds = [...newState.allWorkflowIds, id];
        }
      }
      const workflowMap = _.indexBy(workflowSummaries, 'id');
      const workflowSelector = new WorkflowSelector(newState);
      workflowSelector._mergeSummaryData({workflows: workflowMap});
      return workflowSelector.state;
    }

    case EventConstants.WORKFLOW_ALL_WORKFLOW_TEMPLATES_FETCH_COMPLETED: {
      const {workflowTemplates} = action;
      const newState = {...state};
      // eslint-disable-next-line no-restricted-syntax
      newState.allWorkflowTemplateIds = _.pluck(workflowTemplates, 'id');
      const workflowSelector = new WorkflowSelector(newState);
      workflowSelector._handleUpdateData(workflowTemplates, 'workflowTemplates');
      return workflowSelector.state;
    }
    case EventConstants.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED: {
      const {workflowTemplateVersion, stageConfigurations, stageFlowNodes} = action;
      const workflowSelector = new WorkflowSelector({...state});
      workflowSelector._handleUpdateData([workflowTemplateVersion], 'workflowTemplateVersions');
      workflowSelector._handleUpdateData(Object.values(stageConfigurations), 'stageConfigurations');
      workflowSelector._handleUpdateData(Object.values(stageFlowNodes), 'stageFlowNodes');
      return workflowSelector.state;
    }
    case EventConstants.WORKFLOW_STAGE_CONFIGURATION_FETCH_COMPLETED: {
      const {stageConfiguration} = action;
      const workflowSelector = new WorkflowSelector({...state});
      workflowSelector._handleUpdateData([stageConfiguration], 'stageConfigurations');
      // modifiedAt property of the workflowTemplateVersion was updated as well - refresh its summaryData
      workflowSelector._handleUpdateSummaryData({
        workflowTemplateVersions: {
          [stageConfiguration.workflowTemplateVersion.id]: stageConfiguration.workflowTemplateVersion,
        },
      });
      return workflowSelector.state;
    }
    case EventConstants.FILE_TAG_SCHEMA_UPDATED: {
      const {fileId, tagSchema, tags} = action;
      if (apiutil.apiIdToType(fileId) !== ModelTypeConstants.STAGE_CONFIGURATION) {
        return state;
      }
      const workflowSelector = new WorkflowSelector({...state});
      const stageConfiguration = {
        ...workflowSelector.getStageConfiguration(fileId)!,
        tags,
        tagSchema,
      };
      workflowSelector._handleUpdateData([stageConfiguration], 'stageConfigurations');
      return workflowSelector.state;
    }
    // @ts-expect-error
    case EventConstants.FILE_TAGS_UPDATED: {
      // @ts-expect-error
      const tagData = action.tagData as Array<{id: string; tags: Array<FileConstants.Tag>}>;
      const workflowSelector = new WorkflowSelector({...state});
      const stageConfigurations = tagData
        .filter(({id}) => apiutil.apiIdToType(id) === ModelTypeConstants.STAGE_CONFIGURATION)
        .map(({id, tags}) => ({
          ...workflowSelector.getStageConfiguration(id)!,
          tags,
        }));
      if (stageConfigurations.length >= 1) {
        workflowSelector._handleUpdateData(stageConfigurations, 'stageConfigurations');
      }
      return workflowSelector.state;
    }
    case EventConstants.WORKFLOW_WORKFLOW_DETAILS_FETCH_COMPLETED: {
      const newState = {...state};
      if (action.entryIdToStructuredData != null) {
        newState.entryIdToStructuredData = reactUpdate(newState.entryIdToStructuredData, {
          $merge: action.entryIdToStructuredData,
        });
      }
      if (action.entryIdToSummaryData != null) {
        newState.entryIdToSummaryData = reactUpdate(newState.entryIdToSummaryData, {
          $merge: action.entryIdToSummaryData,
        });
      }
      newState.entryIdToOrderedInputSampleKeys = {
        ...newState.entryIdToOrderedInputSampleKeys,
        ...action.entryIdToOrderedInputSampleKeys,
      };
      newState.entryIdToOrderedOutputSampleKeys = {
        ...newState.entryIdToOrderedOutputSampleKeys,
        ...action.entryIdToOrderedOutputSampleKeys,
      };

      const workflowSelector = new WorkflowSelector(newState);
      workflowSelector._handleUpdateData(Object.values(action.stageRuns), 'stageRuns');
      workflowSelector._handleUpdateData(Object.values(action.stageEntries), 'stageEntries');
      workflowSelector._handleUpdateData(Object.values(action.workflowStages), 'workflowStages');
      workflowSelector._handleUpdateData(Object.values(action.stageConfigurations), 'stageConfigurations');
      workflowSelector._handleUpdateData(Object.values(action.stageFlowNodes), 'stageFlowNodes');
      workflowSelector._handleUpdateData([action.workflow], 'workflows');
      workflowSelector._handleUpdateData([action.workflowTemplateVersion], 'workflowTemplateVersions');
      return workflowSelector.state;
    }
    case EventConstants.WORKFLOW_WORKFLOW_FETCH_COMPLETED: {
      const {workflow} = action;
      const workflowSelector = new WorkflowSelector({...state});
      workflowSelector._handleUpdateData([workflow], 'workflows');
      return workflowSelector.state;
    }
    case EventConstants.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_SUMMARY_FETCH_COMPLETED: {
      const {workflowTemplateVersionSummary} = action;
      const workflowSelector = new WorkflowSelector({...state});
      workflowSelector._handleUpdateSummaryData({
        workflowTemplateVersions: {[workflowTemplateVersionSummary.id]: workflowTemplateVersionSummary},
      });
      return workflowSelector.state;
    }
    case EventConstants.WORKFLOW_WORKFLOW_TEMPLATE_FULL_FETCH_COMPLETED: {
      const {workflowTemplate} = action;
      const workflowSelector = new WorkflowSelector({...state});
      workflowSelector._handleUpdateData([workflowTemplate], 'workflowTemplates');
      return workflowSelector.state;
    }
    case EventConstants.WORKFLOW_SELECT_WORKFLOW_TEMPLATE_VERSION: {
      if (state.viewState.screen === WORKFLOW_SCREEN.WORKFLOW_TEMPLATE_VIEW) {
        const {workflowTemplateVersionId} = action;
        return {
          ...state,
          viewState: reactUpdate(state.viewState, {
            params: {
              selectedWorkflowTemplateVersionId: {$set: workflowTemplateVersionId},
            },
          }),
        };
      }
      return state;
    }
    case EventConstants.WORKFLOW_STAGE_FETCH_COMPLETED: {
      const {workflowStage} = action;
      const workflowSelector = new WorkflowSelector({...state});
      workflowSelector._handleUpdateData([workflowStage], 'workflowStages');
      return workflowSelector.state;
    }
    case EventConstants.WORKFLOW_STAGE_RUNS_FETCH_COMPLETED: {
      const {stageRuns, entryIdToStructuredData} = action;
      const workflowSelector = new WorkflowSelector({...state});
      workflowSelector._handleUpdateData(stageRuns, 'stageRuns');
      return {
        ...workflowSelector.state,
        entryIdToStructuredData: {
          ...workflowSelector.state.entryIdToStructuredData,
          ...(entryIdToStructuredData == null ? {} : entryIdToStructuredData),
        },
      };
    }
    case EventConstants.WORKFLOW_GRAPH_CHANGED: {
      const {
        affectedStageFlowNodes,
        affectedStageEntries,
        affectedStageConfigurations,
        affectedStageRuns,
        entryIdToStructuredData,
      } = action;
      const workflowSelector = new WorkflowSelector({...state});
      workflowSelector._handleUpdateData(affectedStageFlowNodes, 'stageFlowNodes');
      workflowSelector._handleUpdateData(affectedStageEntries, 'stageEntries');
      workflowSelector._handleUpdateData(affectedStageConfigurations, 'stageConfigurations');
      workflowSelector._handleUpdateData(affectedStageRuns, 'stageRuns');
      return {
        ...workflowSelector.state,
        entryIdToStructuredData: {
          ...workflowSelector.state.entryIdToStructuredData,
          ...(entryIdToStructuredData == null ? {} : entryIdToStructuredData),
        },
      };
    }
    case EventConstants.WORKFLOW_STAGE_ENTRIES_FETCH_COMPLETED: {
      const workflowSelector = new WorkflowSelector({...state});
      if (action.stageEntries && action.stageEntries.length > 0) {
        workflowSelector._handleUpdateData(action.stageEntries, 'stageEntries');
      }
      return workflowSelector.state;
    }
    case EventConstants.WORKFLOW_STAGE_RUNS_OUTPUT_SAMPLES_FETCHED: {
      const newState = {...state};
      newState.entryIdToOrderedOutputSampleKeys = {
        ...newState.entryIdToOrderedOutputSampleKeys,
        ...action.entryIdToOrderedOutputSampleKeys,
      };
      const workflowSelector = new WorkflowSelector(newState);
      workflowSelector._handleUpdateData(Object.values(action.stageRuns), 'stageRuns');
      return workflowSelector.state;
    }
    case EventConstants.WORKFLOW_STAGE_ENTRY_DETAILS_FETCH_COMPLETED: {
      const {
        stageEntry,
        stageFlowNodes,
        stageRuns,
        inputSamplesOrder,
        outputSamplesOrder,
        workflowStageOrderedStageRunIds,
      } = action;
      const workflowSelector = new WorkflowSelector({...state});
      workflowSelector._handleUpdateData([stageEntry], 'stageEntries');
      workflowSelector._handleUpdateData(Object.values(stageFlowNodes), 'stageFlowNodes');
      workflowSelector._handleUpdateData(Object.values(stageRuns), 'stageRuns');
      return {
        ...workflowSelector.state,
        entryIdToOrderedInputSampleKeys: {
          ...workflowSelector.state.entryIdToOrderedInputSampleKeys,
          [stageEntry.id]: inputSamplesOrder,
        },
        entryIdToOrderedOutputSampleKeys: {
          ...workflowSelector.state.entryIdToOrderedOutputSampleKeys,
          [stageEntry.id]: outputSamplesOrder,
        },
        stageIdToOrderedStageRunIds: {
          ...workflowSelector.state.stageIdToOrderedStageRunIds,
          [stageEntry.workflowStageId]: workflowStageOrderedStageRunIds,
        },
      };
    }
    case EventConstants.WORKFLOW_STAGE_ENTRY_SAVE_COMPLETED: {
      const {stageEntryId, structuredData, summaryData} = action;
      const newState = {...state};
      if (structuredData != null) {
        newState.entryIdToStructuredData = {
          ...newState.entryIdToStructuredData,
          [stageEntryId]: structuredData,
        };
      }
      if (summaryData != null) {
        newState.entryIdToSummaryData = {...newState.entryIdToSummaryData, [stageEntryId]: summaryData};
      }
      // In most cases, the server returned information is already in the store, so no update is required.
      // By not updating, we improve performance of components that rely on using referential equality to
      // ensure their props are shallow equals and can thus skip renders via PureRenderMixin.
      return isEqual(state, newState) ? state : newState;
    }
    case EventConstants.WORKFLOW_STAGE_RUN_SAMPLES_FETCH_STARTED: {
      const {stageRunSampleIds} = action;
      return {
        ...state,
        loadingStageRunSampleIds: {
          ...state.loadingStageRunSampleIds,
          ..._.object(stageRunSampleIds.map((stageRunSampleId) => [stageRunSampleId, true])),
        },
      };
    }
    case EventConstants.WORKFLOW_STAGE_RUN_SAMPLES_FETCH_COMPLETED: {
      const {stageRunSamples} = action;
      const workflowSelector = new WorkflowSelector({...state});
      workflowSelector._handleUpdateData(stageRunSamples, 'stageRunSamples');
      return {
        ...workflowSelector.state,
        loadingStageRunSampleIds: {
          ...workflowSelector.state.loadingStageRunSampleIds,
          ..._.object(stageRunSamples.map((stageRunSample) => [stageRunSample.id, false])),
        },
      };
    }
    case EventConstants.WORKFLOW_STAGE_FLOW_NODE_FETCH_COMPLETED: {
      const {stageFlowNode, stageRun} = action;
      const workflowSelector = new WorkflowSelector({...state});
      workflowSelector._handleUpdateData([stageFlowNode], 'stageFlowNodes');
      if (stageRun != null) {
        workflowSelector._handleUpdateData([stageRun], 'stageRuns');
      }
      return workflowSelector.state;
    }
    default:
      return state;
  }
}
